/**
 * Export helpers for the activity log.
 *
 * Both functions accept an array of plain string-keyed rows and a filename
 * base (without extension). They trigger an in-browser download via a
 * temporary <a> element.
 *
 * ⚠️ THE CSV MECHANICS MOVED TO `utils/csv.ts` (VERA-260). The escape function that used
 * to live here was the platform's only one until the analytics export needed a second,
 * which is §36's trigger to extract rather than copy. Behaviour is unchanged except that
 * the file now carries a UTF-8 BOM — see that module for why an activity log with a
 * non-ASCII actor name was already mojibake in Excel without it.
 */
import { downloadBlob, toCsvFile } from './csv'

/**
 * Build a CSV string from an array of string-keyed row objects, using the
 * keys of the first row as column headers, then trigger a browser download.
 */
export function exportRowsToCsv(rows: Record<string, string>[], filenameBase: string): void {
  if (rows.length === 0) return

  const headers = Object.keys(rows[0])
  const csv = toCsvFile([headers, ...rows.map(row => headers.map(h => row[h] ?? ''))])

  downloadBlob(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), filenameBase + '.csv')
}

/**
 * Write an XLSX workbook from an array of string-keyed row objects and
 * trigger a browser download. Requires the `xlsx` package (SheetJS).
 */
export async function exportRowsToXlsx(rows: Record<string, string>[], filenameBase: string): Promise<void> {
  if (rows.length === 0) return

  const XLSX = await import('xlsx')
  const ws = XLSX.utils.json_to_sheet(rows)
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Activity')
  XLSX.writeFile(wb, filenameBase + '.xlsx')
}
